import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SPA route-change focus + announcement (WCAG 2.1 AA 2.4.3 Focus Order / 4.1.3
 * Status Messages, bug row 122). In a single-page app a navigation swaps the
 * DOM without a document load, so a screen-reader user gets no signal that the
 * page changed and a keyboard user's focus is stranded wherever it was on the
 * old page. On a genuine route change this hook:
 *   1. moves focus to the new page's <main> landmark (so keyboard Tab restarts
 *      at the content), falling back to the <h1> when <main> is boxless
 *      (`display: contents` — the guide/shared/home wrappers can't take focus),
 *      unless the user has already focused into the new page, and
 *   2. ANNOUNCES the new page (its <h1> text) through a polite live region
 *      (passed in as `announce`) — except when focus landed ON the heading,
 *      whose own focus announcement already carries the name (no double-speak).
 *
 * The boundary is a PATHNAME change, exactly like scroll restoration
 * (lib/scrollRestoration.ts): the effect is keyed on `location.pathname`, so a
 * SAME-pathname transition — the product-detail sheet's `?pdp=N`, a filter
 * param, a `#hash` — never fires it. That's load-bearing: the PDP opens on a
 * same-pathname history push and manages its OWN focus (useDialogA11y); firing
 * route-focus there would yank focus straight back out of the dialog.
 *
 * The FIRST render is skipped — the initial document load already puts the user
 * at the top; only subsequent in-app navigations are announced.
 *
 * Because lazy routes render a Suspense skeleton (no <main>/<h1>) before their
 * real content, the work is deferred through a bounded rAF retry that waits for
 * a real <main> or <h1> to appear, then gives up gracefully after ~1.5s.
 */

// ~1.5s at 60fps — long enough to clear a lazy-chunk fetch + first paint, short
// enough that a route with no landmark gives up instead of spinning forever.
const MAX_FRAMES = 90;

export function useRouteFocus(announce: (message: string) => void): void {
  const location = useLocation();
  // `null` until the first navigation resolves — used to skip the initial load.
  const prevPathnameRef = useRef<string | null>(null);
  // Latch `announce` so a new identity each render can't re-arm the effect.
  const announceRef = useRef(announce);
  useEffect(() => {
    announceRef.current = announce;
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const isFirst = prevPathnameRef.current === null;
    const changed = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;
    // First load, or a re-run that didn't actually change pathname → do nothing.
    if (isFirst || !changed) return undefined;

    let raf = 0;
    let frames = 0;
    const attempt = () => {
      const main = document.querySelector('main');
      const heading =
        (main && main.querySelector('h1')) || document.querySelector('h1');
      if (main || heading) {
        const name = (heading?.textContent || '').replace(/\s+/g, ' ').trim();

        // Don't yank focus if the user already focused a control INSIDE the new
        // content (e.g. Tabbed in while a lazy chunk resolved) — but still
        // announce the change. Focus on a nav link / a persistent tab bar / the
        // body (a link that unmounted on navigation) is NOT "in the content".
        const active = document.activeElement;
        if (main && active && active !== main && main.contains(active)) {
          if (name) announceRef.current(name);
          return;
        }

        // Move focus to restart keyboard navigation at the new content. Prefer
        // <main> (framing the whole region); fall back to the <h1> when <main>
        // is `display: contents` — the guide/shared/home landmark wrappers,
        // which are boxless and so CANNOT take focus (verified in Chrome).
        let landedOnHeading = false;
        if (main) {
          if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
          (main as HTMLElement).focus({ preventScroll: true });
        }
        if ((!main || document.activeElement !== main) && heading) {
          if (!heading.hasAttribute('tabindex')) {
            heading.setAttribute('tabindex', '-1');
          }
          (heading as HTMLElement).focus({ preventScroll: true });
          landedOnHeading = document.activeElement === heading;
        }
        // Announce politely UNLESS we landed focus on the heading — a focused
        // heading is already read aloud, so a live-region echo would double it.
        if (name && !landedOnHeading) announceRef.current(name);
        return;
      }
      if (frames < MAX_FRAMES) {
        frames += 1;
        raf = window.requestAnimationFrame(attempt);
      }
    };
    raf = window.requestAnimationFrame(attempt);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
    };
    // Keyed on pathname alone: same-pathname (?pdp=N / filter / #hash)
    // transitions must NOT fire this (see the module docstring).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}

export default useRouteFocus;
